import * as lockr from 'lockr'
import type { UserDataType } from '../types/UserDataType'

const DB_NAME = 'hayirHahCloudUserDataBackup'
const DB_VERSION = 1
const STORE_NAME = 'cloudUserDataBackups'
const LS_BACKUP_KEY = 'cloud_user_data_backups'
const MAX_BACKUPS = 5

export type CloudUserDataBackupEntry = {
  id: string
  timestamp: number
  userId: string | null
  reason: 'before-merge'
  data: Partial<UserDataType>
}

const isIndexedDBSupported = (): boolean =>
  typeof window !== 'undefined' && window.indexedDB !== undefined

const deepCopy = <T,>(value: T): T => JSON.parse(JSON.stringify(value))

const createBackupEntry = (
  data: Partial<UserDataType>,
  userId: string | null,
  reason: CloudUserDataBackupEntry['reason'],
): CloudUserDataBackupEntry => {
  const timestamp = Date.now()
  return {
    id: `${userId || 'anonymous'}-${reason}-${timestamp}`,
    timestamp,
    userId,
    reason,
    data: deepCopy(data),
  }
}

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (!isIndexedDBSupported()) {
      reject(new Error('IndexedDB is not supported'))
      return
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
        store.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }

    request.onerror = () => {
      reject(request.error || new Error('Cloud backup database could not be opened'))
    }

    request.onsuccess = () => {
      resolve(request.result)
    }
  })

const LocalStorageCloudBackup = {
  async backup(entry: CloudUserDataBackupEntry): Promise<void> {
    const backups: CloudUserDataBackupEntry[] = lockr.get(LS_BACKUP_KEY) || []
    const nextBackups = [entry, ...backups]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, MAX_BACKUPS)
    lockr.set(LS_BACKUP_KEY, nextBackups)
  },

  async getAll(): Promise<CloudUserDataBackupEntry[]> {
    const backups: CloudUserDataBackupEntry[] = lockr.get(LS_BACKUP_KEY) || []
    return backups.sort((a, b) => b.timestamp - a.timestamp)
  },
}

export const backupCloudUserData = async (
  data: Partial<UserDataType> | null,
  userId: string | null,
  reason: CloudUserDataBackupEntry['reason'] = 'before-merge',
): Promise<void> => {
  if (!data || Object.keys(data).length === 0) return

  const entry = createBackupEntry(data, userId, reason)

  if (!isIndexedDBSupported()) {
    return LocalStorageCloudBackup.backup(entry)
  }

  try {
    const db = await openDb()

    return await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)

      store.put(entry)

      const getAllRequest = store.getAll()
      getAllRequest.onsuccess = () => {
        const backups = (getAllRequest.result as CloudUserDataBackupEntry[]).sort(
          (a, b) => b.timestamp - a.timestamp,
        )
        backups.slice(MAX_BACKUPS).forEach((backup) => store.delete(backup.id))
      }

      tx.oncomplete = () => {
        db.close()
        resolve()
      }

      tx.onerror = () => {
        db.close()
        LocalStorageCloudBackup.backup(entry).then(resolve).catch(reject)
      }

      tx.onabort = () => {
        db.close()
        LocalStorageCloudBackup.backup(entry).then(resolve).catch(reject)
      }
    })
  } catch (error) {
    console.error('Failed to backup cloud user data to IndexedDB:', error)
    return LocalStorageCloudBackup.backup(entry)
  }
}

export const getCloudUserDataBackups =
  async (): Promise<CloudUserDataBackupEntry[]> => {
    if (!isIndexedDBSupported()) {
      return LocalStorageCloudBackup.getAll()
    }

    try {
      const db = await openDb()

      return await new Promise<CloudUserDataBackupEntry[]>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly')
        const request = tx.objectStore(STORE_NAME).getAll()

        request.onsuccess = () => {
          resolve(
            (request.result as CloudUserDataBackupEntry[]).sort(
              (a, b) => b.timestamp - a.timestamp,
            ),
          )
        }

        request.onerror = () => {
          reject(request.error || new Error('Cloud backups could not be read'))
        }

        tx.oncomplete = () => db.close()
        tx.onerror = () => db.close()
        tx.onabort = () => db.close()
      })
    } catch (error) {
      console.error('Failed to read cloud user data backups:', error)
      return LocalStorageCloudBackup.getAll()
    }
  }

